import React, { useEffect, useState, useCallback } from "react";
import { Upload, Send, Bell, Trash2, Users, UserCheck, Clock, AlertCircle, RefreshCw, X, Eye, FileText, Mail, Archive, LayoutGrid, Table2 } from "lucide-react";
import { toast } from "sonner";
import "../portfolio/styles.css";

const API = process.env.REACT_APP_BACKEND_URL;

async function api(method, path, body) {
  const opts = { method, headers: { "Content-Type": "application/json" }, credentials: "include" };
  if (body) opts.body = JSON.stringify(body);
  const r = await fetch(`${API}/api/admin/invites${path}`, opts);
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || `Error ${r.status}`); }
  return r.json();
}

const STATUS_COLORS = {
  pending: "bg-gray-100 text-gray-700",
  invited: "bg-blue-50 text-blue-700",
  reminded_1: "bg-amber-50 text-amber-700",
  reminded_2: "bg-orange-50 text-orange-700",
  joined: "bg-green-50 text-green-700",
  bounced: "bg-red-50 text-red-700",
  archived: "bg-stone-100 text-stone-500",
};

const STATUS_LABELS = {
  pending: "Pending",
  invited: "Invited",
  reminded_1: "Reminded (1)",
  reminded_2: "Reminded (2)",
  joined: "Joined",
  bounced: "Bounced",
  archived: "Archived",
};

export default function AdminInvites() {
  const [stats, setStats] = useState(null);
  const [invites, setInvites] = useState([]);
  const [filter, setFilter] = useState("");
  const [importText, setImportText] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [sending, setSending] = useState(false);
  const [reminding, setReminding] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [viewMode, setViewMode] = useState("table");
  const [importSource, setImportSource] = useState("");
  // Draft modal
  const [draftModal, setDraftModal] = useState(null); // { id, email, subject, body_html, loading }
  // Per-row loading
  const [rowLoading, setRowLoading] = useState({}); // { [id]: "sending" | "reminding" | "drafting" }

  const loadStats = useCallback(() => { api("GET", "/stats").then(setStats).catch(() => {}); }, []);
  const loadInvites = useCallback(() => {
    const q = filter ? `?status=${filter}` : "";
    api("GET", q).then(setInvites).catch(() => setInvites([]));
  }, [filter]);

  useEffect(() => { loadStats(); loadInvites(); }, [loadStats, loadInvites]);

  const importEmails = async () => {
    if (!importText.trim()) return toast.error("Paste some email addresses");
    try {
      const d = await api("POST", "/import", { emails: importText, source: importSource || "manual" });
      const parts = [`${d.added} added`];
      if (d.skipped) parts.push(`${d.skipped} duplicates skipped`);
      if (d.archived) parts.push(`${d.archived} archived (generic/support)`);
      if (d.duplicates_in_batch) parts.push(`${d.duplicates_in_batch} duplicates in your paste`);
      toast.success(parts.join(", "));
      setImportText("");
      setShowImport(false);
      setImportSource("");
      loadStats();
      loadInvites();
    } catch (e) { toast.error(e.message); }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    try {
      const d = await api("POST", "/import", { emails: text, source: "csv" });
      const parts = [`${d.added} added from CSV`];
      if (d.skipped) parts.push(`${d.skipped} duplicates`);
      if (d.archived) parts.push(`${d.archived} archived`);
      toast.success(parts.join(", "));
      loadStats();
      loadInvites();
    } catch (err) { toast.error(err.message); }
    e.target.value = "";
  };

  const sendAllInvites = async () => {
    const pending = invites.filter(i => i.status === "pending").length;
    if (!pending) return toast.info("No pending invites to send");
    if (!window.confirm(`Send invite emails to ${pending} people?`)) return;
    setSending(true);
    try {
      const d = await api("POST", "/send", {});
      if (d.status === "started" || d.status === "running") {
        toast.success(`Sending ${d.total} invites in background...`);
        const poll = setInterval(async () => {
          try {
            const s = await api("GET", "/send-status");
            if (!s.running && s.done) {
              clearInterval(poll);
              toast.success(`Done: ${s.sent} sent${s.failed ? `, ${s.failed} failed` : ""}`);
              setSending(false);
              loadStats(); loadInvites();
            }
          } catch { clearInterval(poll); setSending(false); }
        }, 3000);
      } else {
        toast.success(d.message || `Sent ${d.sent || 0}`);
        setSending(false);
        loadStats(); loadInvites();
      }
    } catch (e) { toast.error(e.message); setSending(false); }
  };

  const sendAllReminders = async () => {
    if (!window.confirm("Send reminders to people who haven't joined yet?")) return;
    setReminding(true);
    try {
      const d = await api("POST", "/remind", {});
      const total = (d.reminder_1_sent || 0) + (d.reminder_2_sent || 0);
      if (total === 0) toast.info("No one due for a reminder right now");
      else toast.success(`${d.reminder_1_sent || 0} first + ${d.reminder_2_sent || 0} second reminders sent`);
      loadStats(); loadInvites();
    } catch (e) { toast.error(e.message); }
    setReminding(false);
  };

  const cleanupDuplicates = async () => {
    if (!window.confirm("This will remove duplicate email entries, keeping the most advanced record for each email. Continue?")) return;
    setCleaning(true);
    try {
      const d = await api("POST", "/cleanup-duplicates", {});
      if (d.removed > 0) toast.success(`Removed ${d.removed} duplicate records`);
      else toast.info("No duplicates found");
      loadStats(); loadInvites();
    } catch (e) { toast.error(e.message); }
    setCleaning(false);
  };

  // Single invite actions
  const sendSingle = async (inv) => {
    setRowLoading(p => ({ ...p, [inv.id]: "sending" }));
    try {
      const d = await api("POST", `/${inv.id}/send`, {});
      if (d.sent) toast.success(`Invite sent to ${inv.email}`);
      else toast.error(d.message || "Send failed");
      loadStats(); loadInvites();
    } catch (e) { toast.error(e.message); }
    setRowLoading(p => { const c = { ...p }; delete c[inv.id]; return c; });
  };

  const remindSingle = async (inv) => {
    setRowLoading(p => ({ ...p, [inv.id]: "reminding" }));
    try {
      const d = await api("POST", `/${inv.id}/remind`, {});
      if (d.sent) toast.success(`Reminder ${d.reminder} sent to ${inv.email}`);
      else toast.error("Remind failed");
      loadStats(); loadInvites();
    } catch (e) { toast.error(e.message); }
    setRowLoading(p => { const c = { ...p }; delete c[inv.id]; return c; });
  };

  // Draft / preview
  const openDraft = async (inv) => {
    setRowLoading(p => ({ ...p, [inv.id]: "drafting" }));
    try {
      // If draft already saved, use it
      if (inv.draft_subject && inv.draft_html) {
        setDraftModal({ id: inv.id, email: inv.email, subject: inv.draft_subject, body_html: inv.draft_html, loading: false });
      } else {
        setDraftModal({ id: inv.id, email: inv.email, subject: "", body_html: "", loading: true });
        const d = await api("POST", `/${inv.id}/draft`, {});
        setDraftModal({ id: inv.id, email: inv.email, subject: d.subject, body_html: d.body_html, loading: false });
      }
    } catch (e) { toast.error(e.message); setDraftModal(null); }
    setRowLoading(p => { const c = { ...p }; delete c[inv.id]; return c; });
  };

  const saveDraft = async () => {
    if (!draftModal) return;
    try {
      await api("PUT", `/${draftModal.id}/draft`, { subject: draftModal.subject, body_html: draftModal.body_html });
      toast.success("Draft saved");
      loadInvites();
    } catch (e) { toast.error(e.message); }
  };

  const sendFromDraft = async () => {
    if (!draftModal) return;
    try {
      await api("PUT", `/${draftModal.id}/draft`, { subject: draftModal.subject, body_html: draftModal.body_html });
      const d = await api("POST", `/${draftModal.id}/send`, {});
      if (d.sent) { toast.success(`Invite sent to ${draftModal.email}`); setDraftModal(null); }
      else toast.error(d.message || "Send failed");
      loadStats(); loadInvites();
    } catch (e) { toast.error(e.message); }
  };

  const deleteInvite = async (id) => {
    if (!window.confirm("Remove this invite?")) return;
    try {
      await api("DELETE", `/${id}`);
      loadStats(); loadInvites();
    } catch (e) { toast.error(e.message); }
  };

  const btnPrimary = { background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)", border: "1px solid #B5793F" };

  // Row action buttons per status
  const rowActions = (inv) => {
    const loading = rowLoading[inv.id];
    const actions = [];
    if (inv.status === "pending") {
      actions.push(
        <button key="draft" onClick={() => openDraft(inv)} disabled={!!loading}
                title="Draft & preview email" data-testid={`invite-draft-${inv.id}`}
                className="w-8 h-8 rounded-full border border-blue-200 flex items-center justify-center text-blue-600 hover:bg-blue-50 disabled:opacity-40">
          {loading === "drafting" ? <RefreshCw size={12} className="animate-spin" /> : <Eye size={12} />}
        </button>
      );
      actions.push(
        <button key="send" onClick={() => sendSingle(inv)} disabled={!!loading}
                title="Send invite" data-testid={`invite-send-single-${inv.id}`}
                className="w-8 h-8 rounded-full border border-green-200 flex items-center justify-center text-green-600 hover:bg-green-50 disabled:opacity-40">
          {loading === "sending" ? <RefreshCw size={12} className="animate-spin" /> : <Send size={12} />}
        </button>
      );
    }
    if (inv.status === "invited" || inv.status === "reminded_1") {
      actions.push(
        <button key="remind" onClick={() => remindSingle(inv)} disabled={!!loading}
                title={`Send reminder ${inv.status === "invited" ? "1" : "2"}`} data-testid={`invite-remind-single-${inv.id}`}
                className="w-8 h-8 rounded-full border border-amber-200 flex items-center justify-center text-amber-600 hover:bg-amber-50 disabled:opacity-40">
          {loading === "reminding" ? <RefreshCw size={12} className="animate-spin" /> : <Bell size={12} />}
        </button>
      );
    }
    if (inv.status !== "archived") {
      actions.push(
        <button key="del" onClick={() => deleteInvite(inv.id)} title="Remove" data-testid={`invite-delete-${inv.id}`}
                className="w-8 h-8 rounded-full border border-[#26201620] flex items-center justify-center text-[#8A7E6B] hover:text-red-700 hover:border-red-700">
          <Trash2 size={12} />
        </button>
      );
    }
    return actions;
  };

  return (
    <div data-testid="admin-invites-page">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-8" data-testid="invite-stats">
          <div className="bg-white rounded-2xl p-5 border border-[#d4cbb8]">
            <div className="flex items-center gap-2 text-[#8A7E6B] text-xs font-semibold uppercase tracking-wider mb-1"><Users size={14} /> Total</div>
            <p className="text-3xl font-light text-[#262016]">{stats.total}</p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-[#d4cbb8]">
            <div className="flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-wider mb-1"><Send size={14} /> Sent</div>
            <p className="text-3xl font-light text-[#262016]">{stats.sent}</p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-[#d4cbb8]">
            <div className="flex items-center gap-2 text-amber-600 text-xs font-semibold uppercase tracking-wider mb-1"><Clock size={14} /> Awaiting</div>
            <p className="text-3xl font-light text-[#262016]">{stats.awaiting}</p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-[#d4cbb8]">
            <div className="flex items-center gap-2 text-green-600 text-xs font-semibold uppercase tracking-wider mb-1"><UserCheck size={14} /> Joined</div>
            <p className="text-3xl font-light text-[#262016]">{stats.joined}</p>
            {stats.total > 0 && <p className="text-xs text-[#8A7E6B] mt-1">{Math.round(stats.joined / stats.total * 100)}%</p>}
          </div>
          <div className="bg-white rounded-2xl p-5 border border-[#d4cbb8]">
            <div className="flex items-center gap-2 text-stone-500 text-xs font-semibold uppercase tracking-wider mb-1"><Archive size={14} /> Archived</div>
            <p className="text-3xl font-light text-[#262016]">{stats.archived || 0}</p>
          </div>
        </div>
      )}

      {/* Actions Bar */}
      <div className="flex flex-wrap gap-3 mb-6">
        <button onClick={() => setShowImport(!showImport)} data-testid="invite-import-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[#F6F1E5]" style={btnPrimary}>
          <Upload size={14} /> Import Emails
        </button>
        <button onClick={sendAllInvites} disabled={sending} data-testid="invite-send-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border border-blue-400 text-blue-700 hover:bg-blue-50 disabled:opacity-50">
          {sending ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />} Send All Pending
        </button>
        <button onClick={sendAllReminders} disabled={reminding} data-testid="invite-remind-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border border-amber-400 text-amber-700 hover:bg-amber-50 disabled:opacity-50">
          {reminding ? <RefreshCw size={14} className="animate-spin" /> : <Bell size={14} />} Send All Reminders
        </button>
        <button onClick={cleanupDuplicates} disabled={cleaning} data-testid="invite-cleanup-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50">
          {cleaning ? <RefreshCw size={14} className="animate-spin" /> : <AlertCircle size={14} />} Clean Duplicates
        </button>
        <label className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border border-[#26201625] text-[#262016] hover:border-[#B5793F] cursor-pointer">
          <Upload size={14} /> Upload CSV
          <input type="file" accept=".csv,.txt" onChange={importCsv} className="hidden" data-testid="invite-csv-input" />
        </label>
        <div className="flex rounded-full border border-[#26201625] overflow-hidden ml-auto" data-testid="invite-view-toggle">
          <button onClick={() => setViewMode("cards")}
                  className={`px-3 py-2 text-xs font-semibold ${viewMode === "cards" ? "bg-[#262016] text-[#EDE7D9]" : "text-[#8A7E6B] hover:bg-[#26201608]"}`}>
            <LayoutGrid size={14} />
          </button>
          <button onClick={() => setViewMode("table")}
                  className={`px-3 py-2 text-xs font-semibold ${viewMode === "table" ? "bg-[#262016] text-[#EDE7D9]" : "text-[#8A7E6B] hover:bg-[#26201608]"}`}>
            <Table2 size={14} />
          </button>
        </div>
      </div>

      {/* Import Textarea */}
      {showImport && (
        <div className="bg-white rounded-2xl p-5 border border-[#d4cbb8] mb-6" data-testid="invite-import-form">
          <p className="text-sm font-medium text-[#262016] mb-2">Paste email addresses (comma, newline, or space separated)</p>
          <p className="text-[11px] text-[#8A7E6B] mb-3">Duplicates are auto-skipped. Generic addresses (support@, info@, noreply@, etc.) are auto-archived.</p>
          <div className="mb-3">
            <label className="block text-[11px] uppercase tracking-[0.12em] text-[#8A7E6B] font-medium mb-1.5">Source (where did these emails come from?)</label>
            <input value={importSource} onChange={e => setImportSource(e.target.value)}
                   className="w-full sm:w-1/2 rounded-xl border border-[#26201625] bg-white/80 px-3.5 py-2.5 text-sm text-[#262016] outline-none focus:border-[#B5793F] placeholder-[#8A7E6B]"
                   placeholder="e.g., LinkedIn, Newsletter Signup, Event" data-testid="invite-import-source" />
          </div>
          <textarea value={importText} onChange={(e) => setImportText(e.target.value)}
                    className="w-full h-32 rounded-xl border border-[#26201625] p-3 text-sm text-[#262016] font-mono resize-none focus:outline-none focus:border-[#B5793F]"
                    placeholder="john@example.com, jane@example.com&#10;another@email.com" data-testid="invite-import-textarea" />
          <div className="flex gap-3 mt-3">
            <button onClick={importEmails} className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold text-[#F6F1E5]" style={btnPrimary} data-testid="invite-import-submit">
              Import
            </button>
            <button onClick={() => { setShowImport(false); setImportText(""); }} className="text-sm text-[#8A7E6B] hover:text-[#262016]">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="invite-filters">
        {["", "pending", "invited", "reminded_1", "reminded_2", "joined", "archived"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`invite-filter-${f || "all"}`}
                  className="px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide transition-all"
                  style={{
                    background: f === filter ? "#262016" : "transparent",
                    color: f === filter ? "#EDE7D9" : "#5C5042",
                    border: `1px solid ${f === filter ? "#262016" : "rgba(38,32,22,0.2)"}`,
                  }}>
            {f ? STATUS_LABELS[f] : "All"}
          </button>
        ))}
      </div>

      {/* Invite List */}
      <div data-testid="invite-list">
        {invites.length === 0 && (
          <div className="bg-white/55 rounded-2xl p-8 text-center border border-[#d4cbb8]">
            <Users className="mx-auto mb-3 text-[#8A7E6B]" size={22} />
            <p className="text-sm text-[#8A7E6B]">{filter ? `No ${STATUS_LABELS[filter]?.toLowerCase()} invites` : "Import some emails to get started"}</p>
          </div>
        )}

        {/* Table View */}
        {invites.length > 0 && viewMode === "table" && (
          <div className="bg-white rounded-2xl border border-[#d4cbb8] overflow-x-auto" data-testid="invites-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#d4cbb8] text-[11px] uppercase tracking-[0.12em] text-[#8A7E6B] font-medium">
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Source</th>
                  <th className="text-left px-4 py-3">Invited</th>
                  <th className="text-left px-4 py-3">Reminder 1</th>
                  <th className="text-left px-4 py-3">Reminder 2</th>
                  <th className="text-left px-4 py-3">Joined</th>
                  <th className="text-left px-4 py-3">Added</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invites.map(inv => (
                  <tr key={inv.id} className="border-b border-[#26201610] hover:bg-[#FDFBF6] transition-colors" data-testid={`table-inv-${inv.id}`}>
                    <td className="px-4 py-3 font-medium text-[#262016]">{inv.email}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[inv.status] || "bg-gray-100 text-gray-600"}`}>
                        {STATUS_LABELS[inv.status] || inv.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[#8A7E6B] text-[12px]">{inv.source || "—"}</td>
                    <td className="px-4 py-3 text-[#8A7E6B] text-[12px] whitespace-nowrap">{inv.invited_at ? new Date(inv.invited_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3 text-[#8A7E6B] text-[12px] whitespace-nowrap">{inv.reminded_1_at ? new Date(inv.reminded_1_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3 text-[#8A7E6B] text-[12px] whitespace-nowrap">{inv.reminded_2_at ? new Date(inv.reminded_2_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3 text-[12px] whitespace-nowrap">
                      {inv.joined_at ? <span className="text-green-600">{new Date(inv.joined_at).toLocaleDateString()}</span> : <span className="text-[#8A7E6B]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-[#8A7E6B] text-[12px] whitespace-nowrap">{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">{rowActions(inv)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Card View */}
        {invites.length > 0 && viewMode === "cards" && invites.map((inv) => (
          <div key={inv.id} className="bg-white rounded-2xl flex items-center gap-3 px-5 py-4 border border-[#d4cbb8] shadow-sm" style={{ marginBottom: "14px" }} data-testid={`invite-row-${inv.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#262016] truncate">{inv.email}</p>
              <p className="text-[11px] text-[#8A7E6B] truncate">
                {inv.status === "archived" ? (inv.archive_reason || "Archived") : (
                  <>
                    {inv.invited_at ? `Invited ${new Date(inv.invited_at).toLocaleDateString()}` : "Not yet invited"}
                    {inv.joined_at ? ` · Joined ${new Date(inv.joined_at).toLocaleDateString()}` : ""}
                    {inv.reminded_1_at ? ` · R1: ${new Date(inv.reminded_1_at).toLocaleDateString()}` : ""}
                    {inv.reminded_2_at ? ` · R2: ${new Date(inv.reminded_2_at).toLocaleDateString()}` : ""}
                    {inv.draft_subject ? " · Draft saved" : ""}
                    {inv.source ? ` · Source: ${inv.source}` : ""}
                  </>
                )}
              </p>
            </div>
            <span className={`px-3 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap ${STATUS_COLORS[inv.status] || "bg-gray-100 text-gray-600"}`}>
              {STATUS_LABELS[inv.status] || inv.status}
            </span>
            <div className="flex items-center gap-1.5">
              {rowActions(inv)}
            </div>
          </div>
        ))}
      </div>

      {/* Draft Preview Modal */}
      {draftModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setDraftModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto m-4 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="draft-modal">
            <div className="sticky top-0 bg-white border-b border-[#d4cbb8] px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div>
                <p className="text-sm font-semibold text-[#262016]">Draft Invite for {draftModal.email}</p>
                <p className="text-[11px] text-[#8A7E6B]">Edit the subject and body, then send or save as draft</p>
              </div>
              <button onClick={() => setDraftModal(null)} className="w-8 h-8 rounded-full border border-[#26201620] flex items-center justify-center text-[#8A7E6B] hover:text-[#262016]">
                <X size={14} />
              </button>
            </div>
            {draftModal.loading ? (
              <div className="p-8 text-center">
                <RefreshCw size={20} className="animate-spin mx-auto mb-3 text-[#B5793F]" />
                <p className="text-sm text-[#8A7E6B]">Generating invite with AI...</p>
              </div>
            ) : (
              <div className="p-6">
                <label className="block text-xs font-semibold text-[#8A7E6B] uppercase tracking-wider mb-1">Subject</label>
                <input value={draftModal.subject} onChange={e => setDraftModal(p => ({ ...p, subject: e.target.value }))}
                       className="w-full rounded-xl border border-[#26201625] p-3 text-sm text-[#262016] mb-4 focus:outline-none focus:border-[#B5793F]"
                       data-testid="draft-subject-input" />

                <label className="block text-xs font-semibold text-[#8A7E6B] uppercase tracking-wider mb-1">Email Body (HTML)</label>
                <textarea value={draftModal.body_html} onChange={e => setDraftModal(p => ({ ...p, body_html: e.target.value }))}
                          className="w-full h-40 rounded-xl border border-[#26201625] p-3 text-sm text-[#262016] font-mono resize-none mb-3 focus:outline-none focus:border-[#B5793F]"
                          data-testid="draft-body-input" />

                <label className="block text-xs font-semibold text-[#8A7E6B] uppercase tracking-wider mb-1">Preview</label>
                <div className="rounded-xl border border-[#26201625] p-4 bg-[#FDFBF6] mb-5 text-sm" data-testid="draft-preview"
                     dangerouslySetInnerHTML={{ __html: draftModal.body_html }} />

                <div className="flex gap-3 justify-end">
                  <button onClick={saveDraft} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border border-[#26201625] text-[#262016] hover:border-[#B5793F]" data-testid="draft-save-btn">
                    <FileText size={13} /> Save Draft
                  </button>
                  <button onClick={sendFromDraft} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[#F6F1E5]" style={btnPrimary} data-testid="draft-send-btn">
                    <Send size={13} /> Save & Send
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
